/**
 * ICE server configuration for the browser softphone.
 *
 * PHASE 0 / ITEM 5
 * ----------------
 * Before this module existed, `UaConfig` had no `pcConfig` / `iceServers` field
 * at all, so JsSIP constructed its RTCPeerConnection with an empty ICE server
 * list: host candidates only, no STUN reflexive address, no TURN relay. Any
 * agent behind NAT got no audio. Asterisk's own `stunaddr` (rtp.conf) applies
 * to Asterisk's candidates, not the browser's, so it did not compensate.
 *
 * A "Use STUN" toggle was already rendered in the agent UI and persisted to
 * localStorage, but was never read by any ICE code — it is now honoured here.
 *
 * Configuration comes from Vite env vars so the deployment, not the bundle,
 * decides the topology:
 *   VITE_TURN_URLS      comma-separated, e.g. "turn:turn.example.com:3478"
 *   VITE_TURN_USERNAME  long-term credential username
 *   VITE_TURN_PASSWORD  long-term credential password
 *   VITE_STUN_URLS      comma-separated, defaults to the coturn STUN endpoint
 *
 * OPERATOR NOTE: static TURN credentials shipped to a browser bundle are
 * readable by every agent. Production should mint short-lived credentials
 * server-side (coturn `use-auth-secret`) and serve them per session. That is a
 * deployment decision and is deliberately NOT stubbed here.
 */

export interface IceConfigResult {
  iceServers: RTCIceServer[]
  /** Non-fatal misconfigurations worth surfacing rather than silently degrading. */
  warnings: string[]
}

function splitUrls(raw: string | undefined): string[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((u) => u.trim())
    .filter((u) => u.length > 0)
}

/**
 * Build the ICE server list.
 *
 * Deliberately does NOT throw: a hard failure would brick the softphone on
 * same-LAN dev setups where host candidates are sufficient. Instead it reports
 * loudly via `warnings` (surfaced to the console by the caller) so an
 * unconfigured TURN is visible rather than presenting as working.
 */
export function buildIceServers(opts: { useStun: boolean }): IceConfigResult {
  const env = import.meta.env as Record<string, string | undefined>
  const warnings: string[] = []
  const iceServers: RTCIceServer[] = []

  const turnUrls = splitUrls(env['VITE_TURN_URLS'])
  const turnUsername = env['VITE_TURN_USERNAME']
  const turnPassword = env['VITE_TURN_PASSWORD']

  if (turnUrls.length > 0) {
    if (!turnUsername || !turnPassword) {
      warnings.push(
        'VITE_TURN_URLS is set but VITE_TURN_USERNAME/VITE_TURN_PASSWORD are not. ' +
          'TURN requires credentials; the relay will be unusable and agents behind ' +
          'symmetric NAT will have no audio.',
      )
    } else {
      iceServers.push({
        urls: turnUrls,
        username: turnUsername,
        credential: turnPassword,
      })
    }
  } else {
    warnings.push(
      'No TURN server configured (VITE_TURN_URLS unset). Agents behind symmetric ' +
        'NAT or restrictive firewalls will have NO AUDIO. Configure a TURN server ' +
        'before relying on remote agents.',
    )
  }

  if (opts.useStun) {
    const stunUrls = splitUrls(env['VITE_STUN_URLS'])
    if (stunUrls.length > 0) {
      iceServers.push({ urls: stunUrls })
    } else {
      warnings.push(
        'STUN is enabled but VITE_STUN_URLS is unset, so no reflexive candidate ' +
          'can be gathered.',
      )
    }
  }

  return { iceServers, warnings }
}
